import {
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  inject,
  input,
  output,
  ChangeDetectionStrategy,
} from "@angular/core";
import { noop, Subscription } from "rxjs";
import { SelectItem } from "primeng/api";
import { TeamRepository } from "../../../team/team.repository";
import { Team } from "../../../team/team.model";
import { UserService } from "app/user/user.service";
import { Bind } from "primeng/bind";
import { Select } from "primeng/select";
import { SelectedChipsComponent } from "../../chip/selected-chips.component";
import { Checkbox } from "primeng/checkbox";
import { FormsModule } from "@angular/forms";

export interface TeamChangeEvent {
  originalEvent: MouseEvent;
  value: Team[];
}

@Component({
  selector: "app-team-multiselect",
  templateUrl: "./team-multiselect.component.html",
  styleUrls: ["./team-multiselect.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [Bind, Select, Checkbox, FormsModule, SelectedChipsComponent],
})
export class TeamMultiselectComponent implements OnInit, OnChanges {
  private teamRepository = inject(TeamRepository);
  private userService = inject(UserService);

  public readonly teamChange = output<Team[]>();
  @Input() public teams: Team[] = [];
  public readonly teamIds = input<number[]>([]);
  public readonly hideWhenEmpty = input<boolean>(false);
  public readonly disabled = input(false);
  public readonly required = input(false);
  public readonly placeholder = input<string>("Teams");
  // Restoring a saved selection is silent by default; hosts that drive other
  // requests off the selection opt in to an emit once the list has loaded.
  public readonly emitOnRestore = input(false);
  public readonly selectionLimit = input<number>(undefined);

  public subscription = new Subscription();
  public teamList: SelectItem[] = [];
  public loaded = false;
  private restoreEmitted = false;

  ngOnInit() {
    this.buildTeamList();
  }

  ngOnChanges(changes: SimpleChanges) {
    // Only the saved ids drive a restore; reacting to every input would loop,
    // because the restore emit makes the host hand back a new `teams` array.
    if (!changes["teamIds"]) {
      return;
    }
    const teamIds = this.teamIds();
    if (teamIds && teamIds.length) {
      this.updateSelectedTeams();
    } else if (!changes["teamIds"].isFirstChange()) {
      // Clearing the filter passes an empty list, which `updateSelectedTeams`
      // ignores (it only maps ids onto teams) — drop the chips here.
      this.teams = [];
      this.applySelectionLimit();
    }
  }

  public onTeamChange(event: Team[]) {
    this.applySelectionLimit();
    this.teamChange.emit(event);
  }

  public readonly chipLabel = (team: Team): string => team.getName();
  public readonly chipKey = (team: Team): number => team.getId();

  public removeTeam(team: Team) {
    this.teams = (this.teams ?? []).filter(
      (item) => item.getId() !== team.getId(),
    );
    this.onTeamChange(this.teams);
  }

  public isSelected(team: Team): boolean {
    return (this.teams ?? []).some((item) => item.getId() === team.getId());
  }

  private updateSelectedTeams(): void {
    const teamIds = this.teamIds();
    if (teamIds && teamIds.length && this.teamList.length) {
      const teams = [];
      for (const id of teamIds) {
        const exists = this.teamList.find((item) => item.value.getId() == id);
        if (exists) {
          teams.push(exists.value);
        }
      }
      this.teams = teams;
      this.applySelectionLimit();
      if (this.emitOnRestore() && !this.restoreEmitted) {
        this.restoreEmitted = true;
        this.teamChange.emit(this.teams);
      }
    }
  }

  // p-select has no selectionLimit of its own (that input lives on
  // p-multiselect), so the cap is enforced by disabling the unselected options.
  private applySelectionLimit(): void {
    const limit = this.selectionLimit();
    if (!limit || !this.teamList.length) {
      return;
    }

    const reached = (this.teams ?? []).length >= limit;
    this.teamList = this.teamList.map((item) => ({
      ...item,
      disabled: reached && !this.isSelected(item.value),
    }));
  }

  private buildTeamList(): void {
    this.loaded = false;
    this.teamList = [];

    this.userService.getUser().then((user) => {
      const organizationId = user.getTeam().getOrganization().getId();
      this.teamRepository
        .listByOrganization(organizationId)
        .subscribe((teamList) => {
          teamList.forEach((team) => {
            this.teamList.push({ label: team.getName(), value: team });
          });
          this.teamList = [...this.teamList];
          this.loaded = true;
          this.updateSelectedTeams();
          this.applySelectionLimit();
        });
    }, noop);
  }
}
